import { useEffect, useMemo, useState } from 'react';
import { Modal, TextInput, Stack, Table, Paper, Center, Loader, Text, Group } from '@mantine/core';
import { IconScope } from '../../components/icons/CncIcons';
import { api, imageUrl } from '../../api/client';
import type { Customer } from '../customers/CustomersPage';

export default function CustomerPickerModal({
  onPick,
  onClose,
}: {
  onPick: (c: Customer) => void;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<Customer[] | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    api.get<Customer[]>('/customers').then(setRows);
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((c) => {
      const hay = [c.name, c.legalName, c.address.county, c.address.country].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q]);

  return (
    <Modal opened onClose={onClose} size="lg" title={<span className="modal-title-custom">Select Customer</span>}>
      <Stack>
        <TextInput
          data-tour="picker-search"
          placeholder="Search by name, county, country…"
          leftSection={<IconScope size={14} />}
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          autoFocus
        />
        {!rows ? (
          <Center p="xl"><Loader /></Center>
        ) : filtered.length === 0 ? (
          <Text c="dimmed">No matches.</Text>
        ) : (
          <Paper withBorder style={{ maxHeight: 420, overflow: 'auto' }}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 60 }}>Logo</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th style={{ width: 200 }}>Location</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filtered.map((c) => (
                  <Table.Tr key={c.id} data-tour="picker-row" className="click-row" onClick={() => onPick(c)}>
                    <Table.Td>
                      <div className="image-box">
                        {c.logoPath && <img src={imageUrl(c.logoPath, true)} alt={c.name} />}
                      </div>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Text>{c.name}</Text>
                        {c.legalName && c.legalName !== c.name && <Text size="xs" c="dimmed">({c.legalName})</Text>}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {[c.address.county, c.address.country].filter(Boolean).join(', ') || <Text span c="dimmed">—</Text>}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>
    </Modal>
  );
}
